"use client";

// Merkezi e-ticaret olay katmanı — GA4 + Meta Pixel + TikTok Pixel + Yandex Metrica
import { trackPostHog, posthogPageview } from "@/lib/posthog";
// Tüm olaylar KVKK onayı (cookie-consent) olmadan ateşlenmez.
// Kullanım: trackAddToCart({ id, name, price, quantity }) gibi item'lı çağırın.

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: { track: (event: string, params?: Record<string, unknown>) => void; page?: () => void };
    ym?: (id: number, method: string, ...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export type AnalyticsItem = {
  id: string;
  name: string;
  price: number;
  quantity?: number;
  category?: string;
};

const CURRENCY = "TRY";

function consented(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem("onomac-cookie-consent");
    if (raw === "accepted") return true;
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.status === "accepted";
  } catch {
    return false;
  }
}

export function getYandexId(): number | null {
  const raw = process.env.NEXT_PUBLIC_YANDEX_METRICA_ID;
  const id = raw ? Number(raw) : NaN;
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function getGtmId(): string | null {
  const raw = process.env.NEXT_PUBLIC_GTM_ID?.trim();
  return raw ? raw : null;
}

/** GTM dataLayer aynası — GTM container kurulu değilse dinleyensiz push (zararsız).
 *  GTM'de Custom Event trigger'ları (view_item, add_to_cart, begin_checkout,
 *  purchase) bu event isimlerine bağlanır. */
export function pushToDataLayer(data: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  } catch {}
}

function ymReach(target: string, params?: Record<string, unknown>) {
  const id = getYandexId();
  if (id === null || typeof window === "undefined" || typeof window.ym !== "function") return;
  try {
    window.ym(id, "reachGoal", target, params);
  } catch {}
}

function pushYandexEcommerce(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  try {
    window.dataLayer = window.dataLayer || [];
    // Yandex ecommerce raporları currencyCode ister
    const { ecommerce, ...rest } = payload as { ecommerce?: Record<string, unknown> } & Record<string, unknown>;
    window.dataLayer.push(
      ecommerce ? { ecommerce: { currencyCode: CURRENCY, ...ecommerce }, ...rest } : payload
    );
  } catch {}
}

// ---- Pure builder'lar (test edilebilir, piksel format çevrimleri) ----

export function buildGAItems(items: AnalyticsItem[]) {
  return items.map((i) => ({
    item_id: i.id,
    item_name: i.name,
    item_brand: "Ono MAC",
    price: i.price,
    quantity: i.quantity ?? 1,
    ...(i.category ? { item_category: i.category } : {}),
  }));
}

export function buildMetaContents(items: AnalyticsItem[]) {
  return items.map((i) => ({ id: i.id, quantity: i.quantity ?? 1 }));
}

export function buildTikTokContents(items: AnalyticsItem[]) {
  return items.map((i) => ({
    content_id: i.id,
    content_name: i.name,
    price: i.price,
    quantity: i.quantity ?? 1,
  }));
}

export function buildYandexProducts(items: AnalyticsItem[]) {
  return items.map((i) => ({
    id: i.id,
    name: i.name,
    price: i.price,
    quantity: i.quantity ?? 1,
    ...(i.category ? { category: i.category } : {}),
  }));
}

export function sumValue(items: AnalyticsItem[]): number {
  return Math.round(items.reduce((s, i) => s + i.price * (i.quantity ?? 1), 0) * 100) / 100;
}

function asArray(item: AnalyticsItem | AnalyticsItem[]): AnalyticsItem[] {
  return Array.isArray(item) ? item : [item];
}

// ---- Genel ----

export function initGA(gaId: string) {
  if (typeof window === "undefined" || !gaId) return;
  window.gtag?.("config", gaId, { page_path: window.location.pathname });
}

export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined" || !consented()) return;
  window.gtag?.("event", name, params);
}

/** SPA route geçişlerinde manuel sayfa görüntüleme (tüm pikseller). */
export function trackPageView(path?: string) {
  if (typeof window === "undefined" || !consented()) return;
  const pagePath = path ?? window.location.pathname;
  window.gtag?.("event", "page_view", { page_path: pagePath });
  pushToDataLayer({ event: "page_view", page_path: pagePath });
  window.fbq?.("track", "PageView");
  window.ttq?.page?.();
  const yid = getYandexId();
  if (yid !== null && typeof window.ym === "function") {
    try {
      window.ym(yid, "hit", pagePath);
    } catch {}
  } else {
    pushYandexEcommerce({ event: "page_view", page_path: pagePath });
  }
  posthogPageview(pagePath);
}

// ---- E-ticaret hunisi ----

export function trackViewItem(item: AnalyticsItem) {
  if (typeof window === "undefined" || !consented()) return;
  const payload = {
    currency: CURRENCY,
    value: item.price,
    items: buildGAItems([item]),
  };
  window.gtag?.("event", "view_item", payload);
  pushToDataLayer({ event: "view_item", ecommerce: payload });
  window.fbq?.("track", "ViewContent", {
    content_ids: [item.id],
    content_name: item.name,
    content_type: "product",
    value: item.price,
    currency: CURRENCY,
  });
  window.ttq?.track("ViewContent", {
    content_id: item.id,
    content_name: item.name,
    price: item.price,
    currency: CURRENCY,
  });
  pushYandexEcommerce({ event: "view_item", ecommerce: { detail: { products: buildYandexProducts([item]) } } });
  trackPostHog("view_item", { ...payload });
}

export function trackViewItemList(listName: string, items: AnalyticsItem[]) {
  if (typeof window === "undefined" || !consented() || items.length === 0) return;
  const listPayload = {
    item_list_name: listName,
    items: buildGAItems(items),
  };
  window.gtag?.("event", "view_item_list", listPayload);
  pushToDataLayer({ event: "view_item_list", ecommerce: listPayload });
  window.ttq?.track("ViewContent", {
    content_ids: items.map((i) => i.id),
    description: listName,
  });
  trackPostHog("view_item_list", { ...listPayload });
}

export function trackAddToCart(item: AnalyticsItem | AnalyticsItem[], currency = CURRENCY) {
  const items = asArray(item);
  if (typeof window === "undefined" || !consented() || items.length === 0) return;
  const value = sumValue(items);
  const payload = { currency, value, items: buildGAItems(items) };
  window.gtag?.("event", "add_to_cart", payload);
  pushToDataLayer({ event: "add_to_cart", ecommerce: payload });
  window.fbq?.("track", "AddToCart", {
    content_ids: items.map((i) => i.id),
    contents: buildMetaContents(items),
    content_type: "product",
    value,
    currency,
  });
  window.ttq?.track("AddToCart", {
    contents: buildTikTokContents(items),
    value,
    currency,
  });
  pushYandexEcommerce({ event: "add_to_cart", ecommerce: { add: { products: buildYandexProducts(items) } } });
  ymReach("add_to_cart", { value, currency });
  trackPostHog("add_to_cart", { ...payload });
}

export function trackViewCart(items: AnalyticsItem[], currency = CURRENCY) {
  if (typeof window === "undefined" || !consented() || items.length === 0) return;
  const value = sumValue(items);
  const cartPayload = { currency, value, items: buildGAItems(items) };
  window.gtag?.("event", "view_cart", cartPayload);
  pushToDataLayer({ event: "view_cart", ecommerce: cartPayload });
  // Meta/TikTok'ta standart sepet-görüntüleme olayı yok — custom olarak izlenir
  window.fbq?.("trackCustom", "ViewCart", {
    content_ids: items.map((i) => i.id),
    contents: buildMetaContents(items),
    content_type: "product",
    value,
    currency,
  });
  window.ttq?.track("ViewCart", {
    content_ids: items.map((i) => i.id),
    contents: buildTikTokContents(items),
    value,
    currency,
  });
  pushYandexEcommerce({ event: "view_cart", ecommerce: { cart: { products: buildYandexProducts(items) } } });
  trackPostHog("view_cart", { ...cartPayload });
}

export function trackBeginCheckout(
  items: AnalyticsItem[],
  opts?: { coupon?: string | null; currency?: string }
) {
  const currency = opts?.currency ?? CURRENCY;
  if (typeof window === "undefined" || !consented() || items.length === 0) return;
  const value = sumValue(items);
  const payload = {
    currency,
    value,
    ...(opts?.coupon ? { coupon: opts.coupon } : {}),
    items: buildGAItems(items),
  };
  window.gtag?.("event", "begin_checkout", payload);
  pushToDataLayer({ event: "begin_checkout", ecommerce: payload });
  window.fbq?.("track", "InitiateCheckout", {
    content_ids: items.map((i) => i.id),
    contents: buildMetaContents(items),
    value,
    currency,
    num_items: items.length,
  });
  window.ttq?.track("InitiateCheckout", { value, currency, contents: buildTikTokContents(items) });
  pushYandexEcommerce({ event: "begin_checkout", ecommerce: { checkout: { products: buildYandexProducts(items) } } });
  ymReach("begin_checkout", { value, currency });
  trackPostHog("begin_checkout", { ...payload });
}

export function trackAddPaymentInfo(
  items: AnalyticsItem[],
  opts?: { coupon?: string | null; currency?: string }
) {
  const currency = opts?.currency ?? CURRENCY;
  if (typeof window === "undefined" || !consented() || items.length === 0) return;
  const value = sumValue(items);
  const payload = {
    currency,
    value,
    ...(opts?.coupon ? { coupon: opts.coupon } : {}),
    items: buildGAItems(items),
  };
  window.gtag?.("event", "add_payment_info", payload);
  pushToDataLayer({ event: "add_payment_info", ecommerce: payload });
  window.fbq?.("track", "AddPaymentInfo", {
    content_ids: items.map((i) => i.id),
    contents: buildMetaContents(items),
    value,
    currency,
  });
  window.ttq?.track("AddPaymentInfo", { value, currency, contents: buildTikTokContents(items) });
  ymReach("add_payment_info", { value, currency });
  trackPostHog("add_payment_info", { ...payload });
}

export function trackPurchase(
  order: { orderId: string; items: AnalyticsItem[]; value?: number; coupon?: string | null; shipping?: number; tax?: number },
  currency = CURRENCY
) {
  if (typeof window === "undefined" || !consented() || order.items.length === 0) return;
  const value = order.value ?? sumValue(order.items);
  const payload = {
    transaction_id: order.orderId,
    currency,
    value,
    shipping: order.shipping ?? 0,
    tax: order.tax ?? 0,
    ...(order.coupon ? { coupon: order.coupon } : {}),
    items: buildGAItems(order.items),
  };
  window.gtag?.("event", "purchase", payload);
  pushToDataLayer({ event: "purchase", ecommerce: payload });
  window.fbq?.("track", "Purchase", {
    content_ids: order.items.map((i) => i.id),
    contents: buildMetaContents(order.items),
    value,
    currency,
  });
  window.ttq?.track("CompletePayment", {
    content_ids: order.items.map((i) => i.id),
    contents: buildTikTokContents(order.items),
    value,
    currency,
  });
  pushYandexEcommerce({
    event: "purchase",
    ecommerce: {
      purchase: {
        actionField: { id: order.orderId, revenue: value },
        products: buildYandexProducts(order.items),
      },
    },
  });
  ymReach("purchase", { orderId: order.orderId, value, currency });
  trackPostHog("purchase", { ...payload });
}

export function trackAddToWishlist(item: AnalyticsItem) {
  if (typeof window === "undefined" || !consented()) return;
  window.gtag?.("event", "add_to_wishlist", {
    currency: CURRENCY,
    value: item.price,
    items: buildGAItems([item]),
  });
  window.fbq?.("track", "AddToWishlist", {
    content_ids: [item.id],
    content_name: item.name,
    value: item.price,
    currency: CURRENCY,
  });
  trackPostHog("add_to_wishlist", { currency: CURRENCY, value: item.price, items: buildGAItems([item]) });
}

// ---- Pazarlama / iletişim ----

export function trackSearch(searchTerm: string) {
  if (typeof window === "undefined" || !consented() || !searchTerm.trim()) return;
  window.gtag?.("event", "search", { search_term: searchTerm.trim() });
  window.fbq?.("track", "Search", { search_string: searchTerm.trim() });
  window.ttq?.track("Search", { query: searchTerm.trim() });
  ymReach("search", { query: searchTerm.trim() });
  trackPostHog("search", { search_term: searchTerm.trim() });
}

export function trackLead(source: string) {
  if (typeof window === "undefined" || !consented()) return;
  window.gtag?.("event", "generate_lead", { lead_source: source });
  window.fbq?.("track", "Lead", { content_name: source });
  window.ttq?.track("CompleteRegistration", { description: source });
  ymReach("lead", { source });
  trackPostHog("generate_lead", { lead_source: source });
}

export function trackContact(source: string) {
  if (typeof window === "undefined" || !consented()) return;
  window.gtag?.("event", "contact", { contact_source: source });
  window.fbq?.("track", "Contact", { content_name: source });
  window.ttq?.track("Contact", { description: source });
  ymReach("contact", { source });
  trackPostHog("contact", { contact_source: source });
}
